import React, { useState, useRef, useEffect } from 'react';
import DataLoadingService from '../services/DataLoadingService';
import EmptyStateMessage from '../components/EmptyStateMessage';
import { Storage } from '@capacitor/storage';

import { IonToolbar, IonContent, IonPage, IonButtons, IonTitle, IonMenuButton, IonSegment, IonSegmentButton, IonButton, IonIcon, IonSearchbar, IonRefresher, IonRefresherContent, IonToast, IonModal, IonHeader, getConfig, IonBadge } from '@ionic/react';
import { options } from 'ionicons/icons';

import SessionList from '../components/SessionList';
import SessionListFilter from '../components/SessionListFilter';
import DaySelector from '../components/DaySelector';
import EventInfo from '../components/EventInfo';
import './SchedulePage.scss';
import './SchedulePage.css';
import SafeIonPage from '../components/SafeIonPage';

import * as selectors from '../data/selectors';
import { connect } from '../data/connect';
import { loadConfData, setSearchText } from '../data/sessions/sessions.actions';
import { Schedule } from '../models/Schedule';

interface OwnProps { }

interface StateProps {
  schedule: Schedule;
  favoritesSchedule: Schedule;
  mode: 'ios' | 'md';
  filteredTracks: string[];
  allTracks: string[];
}

interface DispatchProps {
  setSearchText: typeof setSearchText;
  loadConfData: typeof loadConfData;
}

type SchedulePageProps = OwnProps & StateProps & DispatchProps;

const SchedulePage: React.FC<SchedulePageProps> = ({ favoritesSchedule, schedule, setSearchText, loadConfData: loadConfDataAction, mode, filteredTracks, allTracks }) => {
  const [segment, setSegment] = useState<'all' | 'favorites'>('all');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const ionRefresherRef = useRef<HTMLIonRefresherElement>(null);
  const [showCompleteToast, setShowCompleteToast] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0); // Inicialmente 0, se actualizará en el useEffect

  // Estados para el manejo de carga de datos
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasScheduleData, setHasScheduleData] = useState<boolean>(true); // Asumimos que hay datos hasta que se demuestre lo contrario
  const [hasSpeakersData, setHasSpeakersData] = useState<boolean>(true);

  const pageRef = useRef<HTMLElement>(null);

  const ios = mode === 'ios';

  // Verificar si hay filtros activos
  // Se considera que hay filtros activos cuando NO están todos los tracks seleccionados
  const hasActiveFilters = filteredTracks.length < allTracks.length;

  // Extraer las fechas disponibles de los datos de la agenda
  const getAvailableDays = () => {
    if (!schedule || !schedule.groups) return [];

    // Extraer los días únicos de los grupos de la agenda
    const availableDays = schedule.groups
      .map(group => {
        // Extraer la fecha del formato ISO "YYYY-MM-DD"
        const dateStr = group.time;
        if (!dateStr) return null;

        try {
          // Crear un objeto Date a partir de la cadena de fecha en zona horaria local
          // Para evitar problemas de zona horaria, parseamos manualmente la fecha
          const dateParts = dateStr.split('-');
          if (dateParts.length !== 3) return null;

          const year = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1; // Los meses en JavaScript van de 0-11
          const day = parseInt(dateParts[2], 10);

          // Crear la fecha en zona horaria local
          const date = new Date(year, month, day);
          if (isNaN(date.getTime())) return null; // Fecha inválida

          // Obtener el día de la semana (0 = domingo, 1 = lunes, etc.)
          const dayOfWeek = date.getDay();
          // Obtener el día del mes
          const dayNumber = date.getDate();
          // Obtener el mes (0 = enero, 1 = febrero, etc.)
          const monthIndex = date.getMonth();

          // Nombres de los días de la semana en español
          const daysOfWeek = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
          // Nombres de los meses en español
          const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

          // Obtener la abreviatura del día de la semana
          const dayLetter = daysOfWeek[dayOfWeek];
          // Obtener la abreviatura del mes
          const monthName = months[monthIndex];

          return {
            date: dayNumber,
            month: monthName,
            day: dayLetter,
            fullDay: date.toLocaleDateString('es-ES', { weekday: 'long' }),
            fullMonth: date.toLocaleDateString('es-ES', { month: 'long' }),
            groupTime: dateStr,
            dateObj: date // Guardar el objeto Date para ordenar
          };
        } catch (error) {
          console.error('Error al procesar la fecha:', dateStr, error);
          return null;
        }
      })
      .filter(day => day !== null) // Eliminar valores nulos
      .filter((day, index, self) =>
        // Eliminar duplicados basados en la fecha
        index === self.findIndex(d =>
          d?.dateObj?.getDate() === day?.dateObj?.getDate() &&
          d?.dateObj?.getMonth() === day?.dateObj?.getMonth() &&
          d?.dateObj?.getFullYear() === day?.dateObj?.getFullYear()
        )
      )
      .sort((a, b) => a!.dateObj!.getTime() - b!.dateObj!.getTime()); // Ordenar por fecha

    return availableDays;
  };

  // Estado para almacenar los días disponibles
  const [eventDays, setEventDays] = useState<Array<{
    date: number;
    month: string;
    day: string;
    fullDay?: string;
    fullMonth?: string;
    groupTime?: string;
    dateObj?: Date;
    isSelected: boolean;
  }>>([]);

  // Actualizar los días disponibles cuando cambia la agenda
  useEffect(() => {
    if (schedule && schedule.groups) {
      // Obtener los días disponibles
      const availableDays = getAvailableDays();

      // Filtrar los días válidos
      const filteredDays = availableDays
        .filter(day => day !== null &&
                 typeof day.date === 'number' &&
                 typeof day.month === 'string' &&
                 typeof day.day === 'string');

      if (filteredDays.length > 0) {
        // Obtener la fecha actual
        const today = new Date();

        // Buscar si la fecha actual coincide con alguno de los días del evento
        let todayIndex = filteredDays.findIndex(day => {
          // Verificar que day no sea nulo
          if (!day) return false;

          // Verificar que dateObj no sea nulo
          const dateObj = day.dateObj;
          if (!dateObj) return false;

          return dateObj.getDate() === today.getDate() &&
                 dateObj.getMonth() === today.getMonth() &&
                 dateObj.getFullYear() === today.getFullYear();
        });

        // Si no encontramos la fecha actual, seleccionamos el primer día
        if (todayIndex === -1) {
          todayIndex = 0;
        }

        // Actualizar el índice del día seleccionado
        setSelectedDayIndex(todayIndex);

        // Mapear los días disponibles
        const newEventDays = filteredDays.map((day, index) => {
          // TypeScript no reconoce que el filtro ya ha eliminado los valores nulos
          // así que usamos una aserción de tipo para evitar errores
          const safeDay = day as {
            date: number;
            month: string;
            day: string;
            fullDay?: string;
            fullMonth?: string;
            groupTime?: string;
            dateObj?: Date;
          };

          return {
            date: safeDay.date,
            month: safeDay.month,
            day: safeDay.day,
            fullDay: safeDay.fullDay,
            fullMonth: safeDay.fullMonth,
            groupTime: safeDay.groupTime,
            dateObj: safeDay.dateObj,
            isSelected: index === todayIndex
          };
        });

        // Actualizar el estado de los días
        setEventDays(newEventDays);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]); // Solo dependemos de schedule, ya que selectedDayIndex se actualiza dentro del efecto

  // Estado para almacenar la agenda filtrada por día
  // Inicializamos con null para evitar renderizaciones innecesarias
  const [filteredSchedule, setFilteredSchedule] = useState<Schedule | null>(null);

  // La función de filtrado ahora está integrada directamente en el useEffect

  // Función para manejar la selección de un día
  const handleDaySelect = (index: number) => {
    // Actualizamos el índice del día seleccionado
    setSelectedDayIndex(index);

    // Actualizamos manualmente el estado de selección en eventDays
    const updatedDays = eventDays.map((day, i) => ({
      ...day,
      isSelected: i === index
    }));

    setEventDays(updatedDays);
  };

  // Filtrar las sesiones cuando cambian los días disponibles
  useEffect(() => {
    // Inicializar la agenda filtrada cuando cambia la agenda principal o los días disponibles
    if (schedule) {
      // Si tenemos días disponibles y un índice válido, filtrar por día
      if (eventDays.length > 0 && selectedDayIndex < eventDays.length) {
        const selectedDay = eventDays[selectedDayIndex];
        if (selectedDay && selectedDay.dateObj && schedule.groups) {
          // Filtrar los grupos que corresponden al día seleccionado
          const filteredGroups = schedule.groups.filter(group => {
            if (!group.time) return false;

            // Convertir la fecha del grupo a un objeto Date usando el mismo método de parseo
            const dateParts = group.time.split('-');
            if (dateParts.length !== 3) return false;

            const year = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1;
            const day = parseInt(dateParts[2], 10);

            const groupDate = new Date(year, month, day);
            if (isNaN(groupDate.getTime())) return false;

            // Comparar si es el mismo día (mismo año, mes y día)
            // Usar una variable temporal para evitar errores de TypeScript
            const dateObj = selectedDay.dateObj as Date;

            return groupDate.getDate() === dateObj.getDate() &&
                   groupDate.getMonth() === dateObj.getMonth() &&
                   groupDate.getFullYear() === dateObj.getFullYear();
          });

          // Crear una nueva agenda con los grupos filtrados
          setFilteredSchedule({
            ...schedule,
            groups: filteredGroups
          });
        } else {
          setFilteredSchedule(schedule);
        }
      } else {
        // Si no hay días disponibles o el índice no es válido, mostrar toda la agenda
        setFilteredSchedule(schedule);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDays, schedule, selectedDayIndex]); // Incluimos selectedDayIndex como dependencia

  // Efecto para manejar el cambio entre vistas de programa y favoritos
  useEffect(() => {
    // Si cambiamos a la vista de favoritos, no necesitamos filtrar por día
    if (segment === 'favorites') {
      // No hacemos nada con el filtro, solo mostramos todos los favoritos
    } else {
      // Si volvemos a la vista de programa, aplicamos el filtro de día actual
      if (eventDays.length > 0 && selectedDayIndex >= 0) {
        // Disparar el efecto de filtrado actualizando el estado
        setSelectedDayIndex(selectedDayIndex);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment]); // Solo se ejecuta cuando cambia el segmento

  // Efecto para verificar si hay datos disponibles cuando se carga el componente
  useEffect(() => {
    // Verificar si hay datos en la agenda
    if (schedule && (!schedule.groups || schedule.groups.length === 0)) {
      setHasScheduleData(false);
    } else {
      setHasScheduleData(true);
    }
  }, [schedule]);

  const doRefresh = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);

      // Obtener el código de evento actual del almacenamiento
      const { value: eventCode } = await Storage.get({ key: 'eventcode' });

      if (!eventCode) {
        throw new Error('No se encontró código de evento');
      }

      // Usar nuestro servicio de carga de datos
      const dataLoadingService = DataLoadingService.getInstance();
      const result = await dataLoadingService.loadEventData(eventCode, true);

      // Actualizar estados basados en el resultado
      setHasScheduleData(result.hasSchedule);
      setHasSpeakersData(result.hasSpeakers);

      if (!result.success) {
        throw new Error(result.errorMessage || 'Error al cargar datos');
      }

      // Publicar los datos recién cargados en Redux para actualizar la pantalla.
      await loadConfDataAction();

      // Mostrar mensaje de éxito
      setShowCompleteToast(true);
    } catch (error) {
      console.error('Error al refrescar los datos:', error);
      setLoadError(error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setIsLoading(false);

      if (ionRefresherRef.current) {
        ionRefresherRef.current.complete();
      }

      // Ocultar el toast después de un tiempo
      setTimeout(() => {
        setShowCompleteToast(false);
      }, 2500);
    }
  };

  return (
    <SafeIonPage id="schedule-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>

          {ios &&
            <IonSegment value={segment} onIonChange={(e) => setSegment(e.detail.value as any)}>
              <IonSegmentButton value="all">
                PROGRAMA
              </IonSegmentButton>
              <IonSegmentButton value="favorites" className="favorites-segment-button">
                <div className="favorites-content">
                  <span>Favoritos</span>
                  {favoritesSchedule.groups.length > 0 && (
                    <span className="favorites-count">{favoritesSchedule.groups.reduce((count, group) => count + group.sessions.length, 0)}</span>
                  )}
                </div>
              </IonSegmentButton>
            </IonSegment>
          }
        </IonToolbar>

        {!ios &&
          <IonToolbar>
            <IonSegment value={segment} onIonChange={(e) => setSegment(e.detail.value as any)}>
              <IonSegmentButton value="all">
              PROGRAMA
              </IonSegmentButton>
              <IonSegmentButton value="favorites" className="favorites-segment-button">
                <div className="favorites-content">
                  <span>Favoritos</span>
                  {favoritesSchedule.groups.length > 0 && (
                    <span className="favorites-count">{favoritesSchedule.groups.reduce((count, group) => count + group.sessions.length, 0)}</span>
                  )}
                </div>
              </IonSegmentButton>
            </IonSegment>
          </IonToolbar>
        }

        <IonToolbar>
          <div className="search-filter-container">
            <IonSearchbar
              placeholder="Buscar por sesión, speaker, sala o área temática"
              onIonChange={(e: CustomEvent) => setSearchText(e.detail.value)}
              animated={true}
              debounce={300}
              inputmode="search"
              enterkeyhint="search"
              showCancelButton="never"
              style={{
                '--background': document.body.classList.contains('dark-theme') ? '#1e1e1e' : 'rgba(255, 255, 255, 0.8)',
                '--color': document.body.classList.contains('dark-theme') ? 'white' : 'black',
                '--placeholder-color': document.body.classList.contains('dark-theme') ? 'rgba(255, 255, 255, 0.6)' : undefined,
                'border': document.body.classList.contains('dark-theme') ? '1px solid rgba(255, 255, 255, 0.1)' : undefined,
                'borderRadius': '8px'
              }}
            ></IonSearchbar>
            <IonButton
              onClick={() => setShowFilterModal(true)}
              className="filter-button"
              aria-label="Filtrar"
              title="Filtrar"
            >
              <IonIcon icon={options} slot="icon-only" />
              {hasActiveFilters && (
                <IonBadge className="filter-badge" aria-label="Filtros activos"></IonBadge>
              )}
            </IonButton>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>

        <IonRefresher slot="fixed" ref={ionRefresherRef} onIonRefresh={doRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {/* Información del evento seleccionado - Solo mostrar en la vista de programa */}
        {segment === 'all' && <EventInfo />}

        <IonToast
          isOpen={showCompleteToast}
          message="Actualización completada"
          duration={2000}
          onDidDismiss={() => setShowCompleteToast(false)}
        />

        {/* Mostrar mensaje de error si hay un error de carga */}
        {loadError && (
          <EmptyStateMessage
            type="error"
            message={loadError}
            onRefresh={doRefresh}
          />
        )}

        {/* Mostrar mensaje si no hay datos de agenda */}
        {!isLoading && !loadError && segment === 'all' && !hasScheduleData && (
          <EmptyStateMessage
            type="schedule"
            message="No se encontró información de agenda para este evento."
            onRefresh={doRefresh}
          />
        )}

        {/* Selector de días - Solo mostrar si hay días disponibles y estamos en la vista de programa */}
        {eventDays.length > 0 && segment === 'all' && hasScheduleData && (
          <DaySelector days={eventDays} onSelectDay={handleDaySelect} />
        )}

        {/* Lista de sesiones - Solo mostrar si hay datos de agenda */}
        {hasScheduleData && (
          <SessionList
            schedule={filteredSchedule || schedule}
            listType={segment}
            hide={segment === 'favorites'}
          />
        )}

        {/* Lista de favoritos */}
        <SessionList
          schedule={favoritesSchedule}
          listType={segment}
          hide={segment === 'all'}
        />
      </IonContent>

      <IonModal
        isOpen={showFilterModal}
        onDidDismiss={() => setShowFilterModal(false)}
        swipeToClose={true}
        presentingElement={pageRef.current!}
      >
        <SessionListFilter
          onDismissModal={() => setShowFilterModal(false)}
        />
      </IonModal>

    </SafeIonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    schedule: selectors.getSearchedSchedule(state),
    favoritesSchedule: selectors.getGroupedFavorites(state),
    mode: getConfig()!.get('mode'),
    filteredTracks: state.data.filteredTracks,
    allTracks: state.data.allTracks
  }),
  mapDispatchToProps: {
    setSearchText,
    loadConfData
  },
  component: React.memo(SchedulePage)
});
